// src/pages/Admin/Views/Referrals/CommissionPlanForm.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Trash2, Plus, Save, ArrowLeft } from 'lucide-react';
import { createCommissionPlan, getCommissionPlanById, updateCommissionPlan } from '../../../../services/referralApiServices';
import { getCouponsByStatus } from '../../../../services/couponAdminApi';
import { useToast } from '../../../../hooks/use-toast'; //

import { Card, CardHeader, CardTitle, CardContent } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { Checkbox } from '../../../../components/ui/checkbox';

const emptyTier = {
    transaction_index: 1,
    reward_type: 'CASH_PERCENTAGE',
    reward_percentage: '',
    reward_flat_amount: '',
    reward_coupon_id: null,
    referee_reward_coupon_id: null,
    cap_type: 'NONE',
    cap_value: ''
};

const CommissionPlanForm = () => {
    const { planId } = useParams();
    const navigate = useNavigate();
    const { toast } = useToast();
    const isEditing = Boolean(planId);

    const [loading, setLoading] = useState(isEditing);
    const [saving, setSaving] = useState(false);
    const [coupons, setCoupons] = useState([]);
    
    // Plan State
    const [plan, setPlan] = useState({
        name: '',
        description: '',
        target_partner_type: 'AFFILIATE',
        target_package_type: [],
        is_active: true,
        tiers: [{ ...emptyTier }] // Start with 1 empty tier
    });

    useEffect(() => {
        if (isEditing) {
            fetchPlan();
        }
    }, [planId]);

    useEffect(() => {
        getCouponsByStatus('ACTIVE')
            .then(res => setCoupons(res.data || []))
            .catch(() => setCoupons([]));
    }, []);

    const fetchPlan = async () => {
        try {
            const data = await getCommissionPlanById(planId);
            setPlan(data);
        } catch (error) {
            toast({ variant: "destructive", title: "Error", description: "Could not load plan details." });
            navigate('/admin/referrals/commission-plans');
        } finally {
            setLoading(false);
        }
    };

    const handlePackageTypeToggle = (typeStr) => {
        setPlan(prev => {
            const current = prev.target_package_types || [];
            if (current.includes(typeStr)) {
                return { ...prev, target_package_types: current.filter(t => t !== typeStr) };
            } else {
                return { ...prev, target_package_types: [...current, typeStr] };
            }
        });
    };

    const handlePlanChange = (field, value) => {
        setPlan(prev => ({ ...prev, [field]: value }));
    };

    const handleTierChange = (index, field, value) => {
        const newTiers = [...plan.tiers];
        newTiers[index][field] = value;
        
        // Reset irrelevant fields based on reward type
        if (field === 'reward_type') {
            if (value === 'CASH_PERCENTAGE') {
                newTiers[index].reward_flat_amount = '';
                newTiers[index].reward_coupon_id = null;
            }
            if (value === 'CASH_FLAT') {
                newTiers[index].reward_percentage = '';
                newTiers[index].reward_coupon_id = null;
            }
            if (value === 'COUPON_CODE') {
                newTiers[index].reward_percentage = '';
                newTiers[index].reward_flat_amount = '';
            }
        }
        setPlan(prev => ({ ...prev, tiers: newTiers }));
    };

    const addTier = () => {
        const nextIndex = plan.tiers.length > 0 ? Math.max(...plan.tiers.map(t => parseInt(t.transaction_index) || 0)) + 1 : 1;
        setPlan(prev => ({
            ...prev,
            tiers: [...prev.tiers, { ...emptyTier, transaction_index: nextIndex }]
        }));
    };

    const removeTier = (index) => {
        const newTiers = plan.tiers.filter((_, i) => i !== index);
        setPlan(prev => ({ ...prev, tiers: newTiers }));
    };

    const handleSave = async () => {
        if (!plan.name) {
            toast({ variant: "destructive", description: "Plan name is required." });
            return;
        }
        if (plan.tiers.length === 0) {
            toast({ variant: "destructive", description: "You must add at least one tier." });
            return;
        }

        setSaving(true);
        try {
            // Clean up empty strings to nulls for backend constraints
            const payload = {
                ...plan,
                tiers: plan.tiers.map(t => ({
                    ...t,
                    transaction_index: parseInt(t.transaction_index, 10),
                    reward_percentage: t.reward_percentage ? parseFloat(t.reward_percentage) : null,
                    reward_flat_amount: t.reward_flat_amount ? parseFloat(t.reward_flat_amount) : null,
                    reward_coupon_id: t.reward_coupon_id || null,
                    referee_reward_coupon_id: t.referee_reward_coupon_id || null,
                    cap_value: t.cap_value ? parseFloat(t.cap_value) : null,
                }))
            };

            if (isEditing) {
                await updateCommissionPlan(planId, payload);
                toast({ variant: "success", description: "Plan updated successfully!" });
            } else {
                await createCommissionPlan(payload);
                toast({ variant: "success", description: "Plan created successfully!" });
                navigate('/admin/referrals/commission-plans');
            }
        } catch (error) {
            toast({ variant: "destructive", title: "Error", description: error.response?.data?.detail || "Failed to save plan." });
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="p-8 text-center text-muted-foreground">Loading plan...</div>;

    return (
        <div className="p-6 max-w-5xl mx-auto space-y-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/admin/referrals/commission-plans')}>
                        <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <h1 className="text-2xl font-bold text-foreground">
                        {isEditing ? `Edit Plan: ${plan.name}` : "Create New Commission Plan"}
                    </h1>
                </div>
                <Button onClick={handleSave} disabled={saving} variant="default">
                    {saving ? "Saving..." : <><Save className="w-4 h-4 mr-2" /> Save Plan</>}
                </Button>
            </div>

            <Card>
                <CardHeader><CardTitle>1. General Settings</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                        <Label>Plan Name <span className="text-destructive">*</span></Label>
                        <Input value={plan.name} onChange={(e) => handlePlanChange('name', e.target.value)} placeholder="e.g. Standard Affiliate Stagger" />
                    </div>
                    <div className="space-y-2">
                        <Label>Target Partner Group</Label>
                        <Select value={plan.target_partner_type} onValueChange={(v) => handlePlanChange('target_partner_type', v)}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="AFFILIATE">Affiliates</SelectItem>
                                <SelectItem value="SELLER">Sellers</SelectItem>
                                <SelectItem value="ADMIN">Admins / Employees</SelectItem>
                                <SelectItem value="CONTRACTOR">Sales Contractors</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-3 col-span-1 md:col-span-2">
                        <Label>Target Package Types (Select multiple)</Label>
                        <div className="flex gap-6 mt-2">
                            {['WELCOME', 'STANDARD', 'SPECIAL'].map(type => (
                                <div key={type} className="flex items-center space-x-2 bg-muted/30 p-2 rounded-md border">
                                    <Checkbox 
                                        id={`pkg-${type}`}
                                        checked={(plan.target_package_types || []).includes(type)}
                                        onCheckedChange={() => handlePackageTypeToggle(type)}
                                    />
                                    <Label htmlFor={`pkg-${type}`} className="font-normal cursor-pointer">
                                        {type} Set
                                    </Label>
                                </div>
                            ))}
                        </div>
                        <p className="text-xs text-muted-foreground">
                            Leave all options unchecked to apply this plan to ALL package types.
                        </p>
                    </div>
                    <div className="flex items-center space-x-2 mt-8">
                        <Checkbox 
                            id="is_active" 
                            checked={plan.is_active} 
                            onCheckedChange={(checked) => handlePlanChange('is_active', checked)} 
                        />
                        <Label htmlFor="is_active">Plan is Active</Label>
                    </div>
                </CardContent>
            </Card>

            <div className="flex items-center justify-between mt-8 mb-4">
                <h2 className="text-xl font-bold text-foreground">2. Commission Tiers</h2>
                <Button variant="outline" size="sm" onClick={addTier}>
                    <Plus className="w-4 h-4 mr-2" /> Add Tier
                </Button>
            </div>

            <div className="space-y-4">
                {plan.tiers.map((tier, index) => (
                    <Card key={index} className="border-l-4 border-l-primary relative overflow-visible">
                        <Button 
                            variant="destructive" 
                            size="icon" 
                            className="absolute -right-3 -top-3 rounded-full h-8 w-8 shadow-sm"
                            onClick={() => removeTier(index)}
                        >
                            <Trash2 className="w-4 h-4" />
                        </Button>
                        <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                            
                            {/* Transaction Index */}
                            <div className="space-y-2 md:col-span-2">
                                <Label>Applies To Sale #</Label>
                                <Input 
                                    type="number" 
                                    min="0"
                                    value={tier.transaction_index} 
                                    onChange={(e) => handleTierChange(index, 'transaction_index', e.target.value)} 
                                />
                                <p className="text-[10px] text-muted-foreground">0 = All subsequent sales</p>
                            </div>

                            {/* Reward Type */}
                            <div className="space-y-2 md:col-span-3">
                                <Label>Reward Type</Label>
                                <Select value={tier.reward_type} onValueChange={(v) => handleTierChange(index, 'reward_type', v)}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="CASH_PERCENTAGE">Percentage (%)</SelectItem>
                                        <SelectItem value="CASH_FLAT">Flat Amount (Local)</SelectItem>
                                        <SelectItem value="COUPON_CODE">Coupon Code</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Reward Amount (hidden for COUPON_CODE) */}
                            {tier.reward_type !== 'COUPON_CODE' ? (
                                <div className="space-y-2 md:col-span-2">
                                    <Label>Amount</Label>
                                    {tier.reward_type === 'CASH_PERCENTAGE' ? (
                                        <Input type="number" step="0.01" placeholder="e.g. 15.0" value={tier.reward_percentage} onChange={(e) => handleTierChange(index, 'reward_percentage', e.target.value)} />
                                    ) : (
                                        <Input type="number" step="0.01" placeholder="e.g. 500" value={tier.reward_flat_amount} onChange={(e) => handleTierChange(index, 'reward_flat_amount', e.target.value)} />
                                    )}
                                </div>
                            ) : (
                                <div className="md:col-span-2" />
                            )}

                            {/* Dynamic Cap */}
                            <div className="space-y-2 md:col-span-3">
                                <Label>Maximum Cap</Label>
                                <Select value={tier.cap_type} onValueChange={(v) => handleTierChange(index, 'cap_type', v)}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="NONE">No Cap</SelectItem>
                                        <SelectItem value="FIXED_LOCAL_AMOUNT">Fixed Limit</SelectItem>
                                        <SelectItem value="PERCENT_OF_FIRST_SALE">% of 1st Sale</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2 md:col-span-2">
                                <Label>Cap Value</Label>
                                <Input
                                    type="number"
                                    disabled={tier.cap_type === 'NONE'}
                                    value={tier.cap_value}
                                    onChange={(e) => handleTierChange(index, 'cap_value', e.target.value)}
                                />
                            </div>

                            {/* Referrer coupon — only when reward_type is COUPON_CODE */}
                            {tier.reward_type === 'COUPON_CODE' && (
                                <div className="space-y-2 md:col-span-12">
                                    <Label>Referrer Reward Coupon</Label>
                                    <Select
                                        value={tier.reward_coupon_id?.toString() ?? ''}
                                        onValueChange={(v) => handleTierChange(index, 'reward_coupon_id', v ? parseInt(v) : null)}
                                    >
                                        <SelectTrigger><SelectValue placeholder="Select coupon…" /></SelectTrigger>
                                        <SelectContent>
                                            {coupons.map(c => (
                                                <SelectItem key={c.id} value={c.id.toString()}>
                                                    {c.code}{c.description ? ` — ${c.description}` : ''}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}

                            {/* Referee coupon — always optional, seller-referral specific */}
                            <div className="space-y-2 md:col-span-12">
                                <Label>
                                    Referee Welcome Coupon{' '}
                                    <span className="text-muted-foreground text-xs font-normal">(seller referral only — optional)</span>
                                </Label>
                                <Select
                                    value={tier.referee_reward_coupon_id?.toString() ?? 'none'}
                                    onValueChange={(v) => handleTierChange(index, 'referee_reward_coupon_id', v === 'none' ? null : parseInt(v))}
                                >
                                    <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">None</SelectItem>
                                        {coupons.map(c => (
                                            <SelectItem key={c.id} value={c.id.toString()}>
                                                {c.code}{c.description ? ` — ${c.description}` : ''}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                        </CardContent>
                    </Card>
                ))}
                
                {plan.tiers.length === 0 && (
                    <div className="p-8 border-2 border-dashed rounded-lg text-center text-muted-foreground">
                        No tiers defined. Click "Add Tier" to create rules for this plan.
                    </div>
                )}
            </div>
        </div>
    );
};

export default CommissionPlanForm;